import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserRound, Mail, Phone, MapPin, Clock, CalendarPlus, Loader2 } from "lucide-react";

export function AdvisorCard() {
  const [adv, setAdv] = useState(null);
  const [open, setOpen] = useState(false);
  const [time, setTime] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/student/advisor").then(({ data }) => setAdv(data)).catch(() => {}); }, []);
  if (!adv) return null;

  const submit = async () => {
    if (!time.trim()) { toast.error("Please enter a preferred date/time"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/student/appointment", { preferred_time: time, reason });
      toast.success(`Appointment request sent to ${data.advisor}. They'll confirm by email.`);
      setOpen(false); setTime(""); setReason("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Card className="mb-6 p-5 card-elevate" data-testid="advisor-card">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#003057]/8">
          <UserRound className="h-6 w-6 text-[#003057]" />
        </div>
        <div className="min-w-[200px] flex-1">
          <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Your Academic Advisor</p>
          <p className="font-heading text-lg font-bold text-[#003057]">{adv.name}</p>
          <p className="text-xs text-slate-500">{adv.title}</p>
        </div>
        <div className="space-y-1 text-sm text-slate-600">
          <a href={`mailto:${adv.email}`} className="flex items-center gap-2 hover:text-[#005291]" data-testid="advisor-email"><Mail className="h-4 w-4 text-[#78be21]" /> {adv.email}</a>
          {adv.phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#78be21]" /> {adv.phone}</p>}
          {adv.office && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#78be21]" /> {adv.office}</p>}
          {adv.office_hours && <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#78be21]" /> {adv.office_hours}</p>}
        </div>
        <Button onClick={() => setOpen(true)} data-testid="set-appointment-button"
          className="rounded-full bg-[#003057] font-semibold hover:bg-[#004275]">
          <CalendarPlus className="mr-2 h-4 w-4" /> Set an appointment
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[#003057]">Request an appointment with {adv.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="appt-time">Preferred date & time</Label>
              <Input id="appt-time" data-testid="appointment-time" value={time} onChange={(e) => setTime(e.target.value)}
                placeholder="e.g., Tue Jun 23, 2:00 PM" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="appt-reason">What would you like to discuss? (optional)</Label>
              <Textarea id="appt-reason" data-testid="appointment-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Career fit, what-if plan, course selection…" className="mt-1.5" />
            </div>
            <Button onClick={submit} disabled={busy} data-testid="appointment-submit"
              className="w-full rounded-full bg-[#78be21] font-bold text-[#00243f] hover:bg-[#6aac1c]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request appointment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
